import { useCallback, useEffect, useState } from 'react';
import { adminApi, ensureArray } from '../services/api';

type Severity = 'critical' | 'high' | 'moderate' | 'low' | 'info';
type Status =
  | 'detected'
  | 'triaged'
  | 'remediation'
  | 'testing'
  | 'verified'
  | 'resolved'
  | 'accepted';
type Tab = 'findings' | 'sbom' | 'exceptions' | 'settings';

interface Finding {
  id: string;
  packageName: string;
  title: string;
  severity: Severity;
  status: Status;
  vulnerableRange: string | null;
  patchedVersions: string | null;
  recommendedFix: string | null;
  fixTarget: string | null;
  sourceUrl: string | null;
  dependencyPaths: string[];
  upgradePlan: { commands?: string[]; notes?: string[] } | null;
  lastSeenAt: string;
}

interface Summary {
  bySeverity: Record<string, number>;
  openTotal?: number;
  total: number;
}

interface SettingsState {
  scheduleEnabled: boolean;
  scheduleHourUtc: number;
  alertOnCritical: boolean;
  alertOnHigh: boolean;
  lastScheduledAt: string | null;
}

interface SbomRow {
  id: string;
  packageName: string;
  version: string;
  dependencyType: string;
}

interface ExceptionRow {
  id: string;
  reason: string;
  createdAt: string;
  finding: { packageName: string; title: string; severity: string };
}

const SEVERITIES: ('all' | Severity)[] = ['all', 'critical', 'high', 'moderate', 'low', 'info'];
const STATUSES: Status[] = [
  'detected',
  'triaged',
  'remediation',
  'testing',
  'verified',
  'resolved',
  'accepted',
];

function badgeClass(severity: string): string {
  if (severity === 'critical') return 'bg-red-100 text-red-800';
  if (severity === 'high') return 'bg-orange-100 text-orange-800';
  if (severity === 'moderate') return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-700';
}

export function DependencyVulnerabilitiesPage() {
  const [tab, setTab] = useState<Tab>('findings');
  const [findings, setFindings] = useState<Finding[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [howToFix, setHowToFix] = useState<string[]>([]);
  const [settings, setSettings] = useState<SettingsState | null>(null);
  const [sbom, setSbom] = useState<SbomRow[]>([]);
  const [exceptions, setExceptions] = useState<ExceptionRow[]>([]);
  const [severity, setSeverity] = useState<'all' | Severity>('all');
  const [status, setStatus] = useState<'open' | 'all' | Status>('open');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [acceptReason, setAcceptReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setError('');
    const data = await adminApi<{
      findings: Finding[];
      summary: Summary;
      howToFix: string[];
      settings: SettingsState;
    }>('/admin/dependency-vulnerabilities');
    setFindings(ensureArray(data.findings));
    setSummary(data.summary);
    setHowToFix(ensureArray(data.howToFix));
    setSettings(data.settings);
  }, []);

  useEffect(() => {
    load()
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [load]);

  const applyFilters = async () => {
    setError('');
    const params = new URLSearchParams();
    if (status !== 'all') params.set('status', status);
    if (severity !== 'all') params.set('severity', severity);
    if (search.trim()) params.set('q', search.trim());
    const data = await adminApi<{ rows: Finding[] }>(
      `/admin/dependency-vulnerabilities/findings?${params.toString()}`
    );
    setFindings(ensureArray(data.rows));
  };

  const scan = async () => {
    setScanning(true);
    setError('');
    setNotice('');
    try {
      const result = await adminApi<{ newFindingCount: number; clearedCount: number }>(
        '/admin/dependency-vulnerabilities/scan',
        { method: 'POST' }
      );
      setNotice(`Scan finished. ${result.newFindingCount} new, ${result.clearedCount} cleared.`);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setScanning(false);
    }
  };

  const setFindingStatus = async (id: string, next: Status) => {
    setError('');
    try {
      await adminApi(`/admin/dependency-vulnerabilities/findings/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: next }),
      });
      await applyFilters();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const accept = async (id: string) => {
    setError('');
    try {
      await adminApi(`/admin/dependency-vulnerabilities/findings/${id}/accept`, {
        method: 'POST',
        body: JSON.stringify({ reason: acceptReason }),
      });
      setAcceptReason('');
      await applyFilters();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const loadSbom = async () => {
    const data = await adminApi<{ rows: SbomRow[] }>(
      '/admin/dependency-vulnerabilities/sbom?limit=100'
    );
    setSbom(ensureArray(data.rows));
  };

  const loadExceptions = async () => {
    const data = await adminApi<ExceptionRow[]>('/admin/dependency-vulnerabilities/exceptions');
    setExceptions(ensureArray(data));
  };

  const saveSettings = async () => {
    if (!settings) return;
    setError('');
    try {
      const saved = await adminApi<SettingsState>('/admin/dependency-vulnerabilities/settings', {
        method: 'PUT',
        body: JSON.stringify({
          scheduleEnabled: settings.scheduleEnabled,
          scheduleHourUtc: Number(settings.scheduleHourUtc),
          alertOnCritical: settings.alertOnCritical,
          alertOnHigh: settings.alertOnHigh,
        }),
      });
      setSettings(saved);
      setNotice('Scan schedule saved.');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (loading) return <div className="text-center text-gray-500">Loading...</div>;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dependency vulnerabilities</h1>
          <p className="mt-1 text-sm text-gray-500">
            Findings come from npm audit against the lockfile. Nothing is upgraded from this screen.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void scan()}
          disabled={scanning}
          className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {scanning ? 'Scanning…' : 'Run scan'}
        </button>
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      {notice && <p className="mt-3 text-sm text-green-700">{notice}</p>}

      <div className="mt-4 flex flex-wrap gap-2 text-sm">
        <span className="rounded-full bg-gray-100 px-3 py-1">Open {summary?.openTotal ?? 0}</span>
        <span className="rounded-full bg-red-100 px-3 py-1 text-red-800">
          Critical {summary?.bySeverity.critical ?? 0}
        </span>
        <span className="rounded-full bg-orange-100 px-3 py-1 text-orange-800">
          High {summary?.bySeverity.high ?? 0}
        </span>
        <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">
          Moderate {summary?.bySeverity.moderate ?? 0}
        </span>
      </div>

      <div className="mt-4 flex gap-2 text-sm">
        {(['findings', 'sbom', 'exceptions', 'settings'] as Tab[]).map((item) => (
          <button
            key={item}
            type="button"
            className={`rounded-lg px-3 py-1.5 font-medium ${tab === item ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`}
            onClick={() => {
              setTab(item);
              if (item === 'sbom') void loadSbom().catch((err: Error) => setError(err.message));
              if (item === 'exceptions')
                void loadExceptions().catch((err: Error) => setError(err.message));
            }}
          >
            {item[0]?.toUpperCase()}
            {item.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'findings' && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-2">
            <select
              className="rounded-lg border px-2 py-1 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'open' | 'all' | Status)}
            >
              <option value="open">Open</option>
              <option value="all">All statuses</option>
              {STATUSES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            <select
              className="rounded-lg border px-2 py-1 text-sm"
              value={severity}
              onChange={(e) => setSeverity(e.target.value as 'all' | Severity)}
            >
              {SEVERITIES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            <input
              className="rounded-lg border px-2 py-1 text-sm"
              placeholder="Search package or title"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              className="rounded-lg border px-3 py-1 text-sm"
              onClick={() => void applyFilters()}
            >
              Filter
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {findings.map((finding) => (
              <article key={finding.id} className="rounded-xl border border-gray-200 p-4">
                <button
                  type="button"
                  className="flex w-full items-start justify-between gap-3 text-left"
                  onClick={() => setExpandedId(expandedId === finding.id ? null : finding.id)}
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-bold uppercase ${badgeClass(finding.severity)}`}
                      >
                        {finding.severity}
                      </span>
                      <span className="text-xs uppercase text-gray-500">{finding.status}</span>
                    </div>
                    <h2 className="mt-1 font-semibold text-gray-900">{finding.packageName}</h2>
                    <p className="text-sm text-gray-600">{finding.title}</p>
                  </div>
                  <span className="text-xs text-gray-400">
                    {new Date(finding.lastSeenAt).toLocaleString()}
                  </span>
                </button>
                {expandedId === finding.id && (
                  <div className="mt-3 space-y-2 text-sm text-gray-700">
                    <p>Vulnerable range: {finding.vulnerableRange ?? '—'}</p>
                    <p>Patched versions: {finding.patchedVersions ?? '—'}</p>
                    <p>Recommended fix: {finding.recommendedFix ?? '—'}</p>
                    {finding.sourceUrl && (
                      <a
                        className="text-indigo-700 underline"
                        href={finding.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Advisory
                      </a>
                    )}
                    {finding.upgradePlan?.commands && (
                      <pre className="overflow-x-auto rounded bg-gray-50 p-3 text-xs">
                        {finding.upgradePlan.commands.join('\n')}
                      </pre>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {STATUSES.filter((item) => item !== 'accepted').map((item) => (
                        <button
                          key={item}
                          type="button"
                          className="rounded border px-2 py-1 text-xs"
                          onClick={() => void setFindingStatus(finding.id, item)}
                        >
                          Mark {item}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        className="flex-1 rounded border px-2 py-1 text-sm"
                        placeholder="Reason to accept this risk"
                        value={acceptReason}
                        onChange={(e) => setAcceptReason(e.target.value)}
                      />
                      <button
                        type="button"
                        className="rounded bg-gray-900 px-3 py-1 text-xs text-white"
                        onClick={() => void accept(finding.id)}
                      >
                        Accept risk
                      </button>
                    </div>
                  </div>
                )}
              </article>
            ))}
            {findings.length === 0 && (
              <p className="text-sm text-gray-500">
                No findings for this filter. Run a scan to refresh.
              </p>
            )}
          </div>

          {howToFix.length > 0 && (
            <ul className="mt-6 list-disc space-y-1 pl-5 text-sm text-gray-600">
              {howToFix.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === 'sbom' && (
        <table className="mt-4 w-full text-left text-sm">
          <thead>
            <tr className="border-b text-gray-600">
              <th className="pb-2">Package</th>
              <th className="pb-2">Version</th>
              <th className="pb-2">Type</th>
            </tr>
          </thead>
          <tbody>
            {sbom.map((row) => (
              <tr key={row.id} className="border-b border-gray-100">
                <td className="py-2">{row.packageName}</td>
                <td className="py-2">{row.version}</td>
                <td className="py-2">{row.dependencyType}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === 'exceptions' && (
        <div className="mt-4 space-y-3">
          {exceptions.map((row) => (
            <article key={row.id} className="rounded-xl border p-4 text-sm">
              <p className="font-semibold">{row.finding.packageName}</p>
              <p className="text-gray-600">{row.finding.title}</p>
              <p className="mt-1">{row.reason}</p>
            </article>
          ))}
          {exceptions.length === 0 && (
            <p className="text-sm text-gray-500">No active exceptions.</p>
          )}
        </div>
      )}

      {tab === 'settings' && settings && (
        <div className="mt-4 max-w-md space-y-3 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.scheduleEnabled}
              onChange={(e) => setSettings({ ...settings, scheduleEnabled: e.target.checked })}
            />
            Daily scan enabled
          </label>
          <label className="block">
            Hour (UTC)
            <input
              type="number"
              min={0}
              max={23}
              className="mt-1 w-24 rounded border px-2 py-1"
              value={settings.scheduleHourUtc}
              onChange={(e) =>
                setSettings({ ...settings, scheduleHourUtc: Number(e.target.value) })
              }
            />
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.alertOnCritical}
              onChange={(e) => setSettings({ ...settings, alertOnCritical: e.target.checked })}
            />
            Alert on new critical findings
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.alertOnHigh}
              onChange={(e) => setSettings({ ...settings, alertOnHigh: e.target.checked })}
            />
            Alert on new high findings
          </label>
          <p className="text-gray-500">
            Last scheduled run:{' '}
            {settings.lastScheduledAt ? new Date(settings.lastScheduledAt).toLocaleString() : '—'}
          </p>
          <button
            type="button"
            className="rounded-lg bg-gray-900 px-3 py-2 text-white"
            onClick={() => void saveSettings()}
          >
            Save settings
          </button>
        </div>
      )}
    </div>
  );
}
